package com.flowyride.core.device

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.navigation.LocationFix
import com.flowyride.core.domain.navigation.LocationSource
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.flow.emptyFlow
import kotlin.js.Date

actual fun platformNavigationDevice(): NavigationDevice = BrowserNavigationDevice

/** The web app only simulates rides: a browser cannot follow the GPS with the screen off. */
private object BrowserNavigationDevice : NavigationDevice {
    override val supportsNavigation: Boolean = false

    override val location: LocationSource = object : LocationSource {
        override fun fixes(): Flow<LocationFix> = callbackFlow {
            val geolocation = js("navigator.geolocation")
            val id = geolocation.watchPosition(
                { position: dynamic ->
                    val coords = position.coords
                    trySend(
                        LocationFix(
                            point = GeoPoint(coords.latitude as Double, coords.longitude as Double),
                            timeMillis = (position.timestamp as Number).toLong(),
                            bearing = (coords.heading as? Number)?.toDouble()?.takeIf { !it.isNaN() },
                            speedMetersPerSecond = (coords.speed as? Number)?.toDouble(),
                            accuracyMeters = (coords.accuracy as? Number)?.toDouble(),
                        ),
                    )
                },
                { _: dynamic -> },
                js("({ enableHighAccuracy: true })"),
            )
            awaitClose { geolocation.clearWatch(id) }
        }
    }

    override val roughLocation: LocationSource get() = location

    /** Unknown without asking: the browser asks on the first request (button). */
    override val hasLocationPermission: Boolean = false

    override val stopRequests: Flow<Unit> = emptyFlow()

    override fun startBackgroundNavigation(text: String) {}
    override fun updateBackgroundNavigation(text: String) {}
    override fun stopBackgroundNavigation() {}

    override fun speak(text: String) {
        val synthesis = js("window.speechSynthesis") ?: return
        val utterance = js("new SpeechSynthesisUtterance(text)")
        utterance.lang = "fr-FR"
        synthesis.cancel()
        synthesis.speak(utterance)
    }
}

@Composable
actual fun NavigationWindowEffect() {
    DisposableEffect(Unit) {
        // Screen Wake Lock API, where available.
        var lock: dynamic = null
        val wakeLock = js("navigator.wakeLock")
        if (wakeLock != null && wakeLock != undefined) {
            wakeLock.request("screen").then({ l: dynamic -> lock = l }, { _: dynamic -> })
        }
        onDispose { lock?.release() }
    }
}

/** The browser asks by itself on the first position request. */
@Composable
actual fun rememberNavigationPermissions(onResult: (granted: Boolean) -> Unit): () -> Unit =
    remember { { onResult(true) } }

actual fun formatClockTime(epochMillis: Long): String {
    val date = Date(epochMillis.toDouble())
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}

/** The browser asks by itself on the first position request. */
@Composable
actual fun rememberLocationPermission(onResult: (granted: Boolean) -> Unit): () -> Unit =
    remember { { onResult(true) } }

/** The browser draws its own bar. */
@Composable
actual fun StatusBarIconsEffect(darkIcons: Boolean) {}
